import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { range, last } from 'lodash';
import accounting from 'accounting';
import classNames from 'classnames';
import Table from 'components/v2/Table';
import { useTranslation } from 'react-i18next';

const renderStepValue = (value, fscStepUnit, t) => {
  if (fscStepUnit === t('COST_PER_MILE')) {
    return t('COST_PER_MILE_VALUE', { value });
  }

  return `${value}${fscStepUnit}`;
};

export default function FSCPreview({
  data: {
    fuelPriceMin,
    fuelPriceMax,
    fuelPriceStepInterval,
    fscMin,
    fscStepInterval,
    fscStepUnit,
  },
  rowsPerTable,
}) {
  const [showSecondColumn, setShowSecondColumn] = useState(false);
  const [showThirdColumn, setShowThirdColumn] = useState(false);
  const [secondColumnThreshold, setSecondColumnThreshold] = useState(0);
  const [thirdColumnThreshold, setThirdColumnThreshold] = useState(0);
  const [total, setTotal] = useState(0);
  const [visibleMaxPrice, setVisibleMaxPrice] = useState(fuelPriceMax);
  const [previewData, setPreviewData] = useState([]);
  const { t } = useTranslation();

  function buildPreview() {
    const items = range(
      parseFloat(fuelPriceMin),
      parseFloat(fuelPriceMax),
      parseFloat(fuelPriceStepInterval),
    )
      .map((value, i) => {
        return {
          from: value,
          to: value + fuelPriceStepInterval,
          surcharge: fscMin + i * fscStepInterval,
        };
      })
      .slice(0, rowsPerTable * 3);

    const lastItem = last(items)?.to;
    setVisibleMaxPrice(lastItem);
    setPreviewData(items);

    const total = items.length;
    setTotal(total);

    const secondColumnThreshold = rowsPerTable * 1 + 1;
    const thirdColumnThreshold = rowsPerTable * 2 + 1;
    setSecondColumnThreshold(secondColumnThreshold);
    setThirdColumnThreshold(thirdColumnThreshold);

    const showSecondColumn = total >= secondColumnThreshold;
    const showThirdColumn = total >= thirdColumnThreshold;
    setShowSecondColumn(showSecondColumn);
    setShowThirdColumn(showThirdColumn);
  }

  useEffect(() => {
    buildPreview();
  }, [
    fuelPriceMin,
    fuelPriceMax,
    fuelPriceStepInterval,
    fscMin,
    fscStepInterval,
  ]);

  const previewColumns = [
    {
      field: 'from',
      label: t('AT_LEAST'),
      render: (value) => {
        return accounting.formatMoney(value);
      },
    },
    {
      field: 'to',
      label: t('BUT_LESS_THAN'),
      render: (value) => {
        return accounting.formatMoney(value);
      },
    },
    {
      field: 'surcharge',
      label: t('SURCHARGE'),
      render: (value) => renderStepValue(value.toFixed(2), fscStepUnit, t),
    },
  ];

  function getFirstColumn() {
    return previewData.slice(0, rowsPerTable) || [];
  }

  function getSecondColumn() {
    const start = rowsPerTable;
    const end = start + rowsPerTable;
    return previewData.slice(start, end);
  }

  function getThirdColumn() {
    const start = rowsPerTable * 2;
    const end = start + rowsPerTable;
    return previewData.slice(start, end);
  }

  const gridColumnsClass = classNames(
    'lg:overflow-x-auto lg:grid lg:rounded-lg lg:border lg:border-gray-200',
    {
      'grid-cols-3': total >= thirdColumnThreshold,
      'grid-cols-2':
        total >= secondColumnThreshold && total < thirdColumnThreshold,
      'grid-cols-1': total < secondColumnThreshold,
    },
  );

  const comment = useMemo(() => {
    const fuelPriceStepIntervalValue = `<strong>${accounting.formatMoney(
      fuelPriceStepInterval,
    )}</strong>`;

    const visibleMaxPriceValue = `<strong>${accounting.formatMoney(
      visibleMaxPrice,
    )}/gallon</strong>`;

    const fscStepIntervalValue = `<strong>${renderStepValue(
      accounting.formatNumber(fscStepInterval, 2),
      fscStepUnit,
      t,
    )}</strong>`;

    // 'Fuel rates above the shown above shall be assessed as follows: ' +
    // 'For each {{fuelPriceStepInterval}} increase in fuel rates above {{visibleMaxPrice}}, ' +
    // '{{fscStepInterval}} will be added to the rate shown, up to the maximum fuel price set above.',

    return t('FSC_PREVIEW_COMMENT', {
      fuelPriceStepInterval: fuelPriceStepIntervalValue,
      visibleMaxPrice: visibleMaxPriceValue,
      fscStepInterval: fscStepIntervalValue,
      interpolation: { escapeValue: false },
    });
  }, [fuelPriceStepInterval, visibleMaxPrice, fscStepInterval]);

  return (
    <>
      <div className={gridColumnsClass}>
        <Table
          className="mb-4 lg:mb-0 rounded-lg lg:rounded-none"
          columns={previewColumns}
          data={getFirstColumn()}
          headers={previewColumns}
        />
        {showSecondColumn && (
          <Table
            className="mb-4 lg:mb-0 rounded-lg lg:rounded-none"
            columns={previewColumns}
            data={getSecondColumn()}
            headers={previewColumns}
          />
        )}
        {showThirdColumn && (
          <Table
            className="mb-4 lg:mb-0 rounded-lg lg:rounded-none"
            columns={previewColumns}
            data={getThirdColumn()}
            headers={previewColumns}
          />
        )}
      </div>
      <p
        className="mt-2 text-xs italic text-gray-500"
        dangerouslySetInnerHTML={{ __html: comment }}
      />
    </>
  );
}

FSCPreview.propTypes = {
  rowsPerTable: PropTypes.number,
  data: PropTypes.shape({
    fuelPriceMin: PropTypes.number,
    fuelPriceMax: PropTypes.number,
    fuelPriceStepInterval: PropTypes.number,
    fscMin: PropTypes.number,
    fscStepInterval: PropTypes.number,
  }),
};

FSCPreview.defaultProps = {
  rowsPerTable: 10,
};
